import { JSX, useState, ChangeEvent } from 'react';
import {
    useRecordContext,
    useRefresh,
    Confirm,
    useNotify
} from 'react-admin';
import {
    Box,
    TextField,
    Button,
    CircularProgress,
    Typography
} from '@mui/material';
import { CustomerAccount } from "../../../../types/entities";
import { useCustomerAccountCreditManager } from "./hooks/customer-account-credit-manager";

const MONO: string = '"Source Code Pro Regular", ui-monospace, SFMono-Regular, Menlo, monospace';

export const CustomerAccountCreditManager: () => JSX.Element = (): JSX.Element => {
    const record: CustomerAccount = useRecordContext();
    const [openConfirm, setOpenConfirm] = useState<boolean>(false);
    const [creditCount, setCreditCount] = useState<number>(0);
    const refresh: () => void = useRefresh();
    const notify: any = useNotify();
    const { loading, processCreditUpdate } = useCustomerAccountCreditManager();

    function handleConfirmShow(value: boolean): void {
        setOpenConfirm(value);
    }

    // Clearing the field gave parseInt an empty string, so creditCount became
    // NaN — and `NaN <= 0` is false, so the guard below let it through and the
    // confirmation read "add NaN credits".
    function handleCreditAmountChange(event: ChangeEvent<HTMLInputElement>): void {
        const parsed: number = parseInt(event.currentTarget.value, 10);
        setCreditCount(Number.isFinite(parsed) ? parsed : 0);
    }

    const canSubmit: boolean = !loading && creditCount > 0;
    const balance: number = Number(record?.credits ?? 0);

    function handleConfirm(): void {
        if (!(creditCount > 0)) {
            notify('Enter a number of credits above 0 to add', { type: 'error' });
            handleConfirmShow(false);
            return;
        }
        handleConfirmShow(false);
        processCreditUpdate(record.id, creditCount)
            .then((data: any): void => {
                if (data?.status === 'success') {
                    notify('Credits have been successfully added', { type: 'success' });
                    setCreditCount(0);
                    refresh();
                }
                if (data?.status === 'error') {
                    notify(data?.message, { type: 'error' });
                }
            });
    }

    return (
        <>
            { /* Was a card ringed in lightgreen with a 6px green drop shadow,
                 and a TextField wrapped in its own green border fighting MUI's
                 outline. The balance is the thing worth seeing, so it gets the
                 size; adding credits is an ordinary form beside it. */ }
            <Box
                sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'flex-end',
                    justifyContent: 'space-between',
                    gap: 2,
                    width: '100%',
                    mb: 3,
                    p: 2,
                    borderRadius: 3,
                    border: 1,
                    borderColor: 'divider',
                    bgcolor: 'background.paper',
                }}
            >
                <Box sx={{ minWidth: 0 }}>
                    <Typography
                        component="h3"
                        sx={{
                            fontFamily: MONO,
                            fontSize: 10,
                            letterSpacing: '0.14em',
                            textTransform: 'uppercase',
                            color: 'text.secondary',
                        }}
                    >
                        { 'Credits' }
                    </Typography>
                    <Typography
                        sx={{
                            fontFamily: MONO,
                            fontSize: 30,
                            lineHeight: 1.15,
                            fontWeight: 600,
                            color: 'text.primary',
                        }}
                    >
                        { balance.toLocaleString() }
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: 'text.disabled' }}>
                        { 'available on this account' }
                    </Typography>
                </Box>

                { /* Field and button share an explicit height so they sit on the
                     same line — a small TextField and a default Button do not
                     agree on one, so they stepped past each other. */ }
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    { /* react-admin's theme defaults TextField to the filled
                         variant, which is right for the stacked form fields but
                         gives this inline control an underline that sits below
                         the button beside it. Outlined matches the button. */ }
                    <TextField
                        type="number"
                        size="small"
                        variant="outlined"
                        label="Add credits"
                        value={ creditCount === 0 ? '' : creditCount }
                        onChange={ handleCreditAmountChange }
                        sx={{
                            width: 170,
                            '& .MuiInputBase-root': { height: 44, borderRadius: 2 },
                        }}
                        slotProps={{ htmlInput: { min: 1, step: 1, 'aria-label': 'Credits to add' } }}
                    />
                    <Button
                        variant="contained"
                        disableElevation
                        disabled={ !canSubmit }
                        onClick={ (): void => handleConfirmShow(true) }
                        sx={{ height: 44, textTransform: 'none', fontWeight: 600, borderRadius: 2, px: 3 }}
                    >
                        { 'Add' }
                        { loading &&
                            <CircularProgress size={ 16 } sx={{ ml: 1, color: 'inherit' }} />
                        }
                    </Button>
                </Box>
            </Box>

            <Confirm
                isOpen={ openConfirm }
                title="Add credits"
                content={ `Add ${creditCount.toLocaleString()} credit${creditCount === 1 ? '' : 's'} to this account? The new balance will be ${(balance + creditCount).toLocaleString()}.` }
                onConfirm={ handleConfirm }
                onClose={ (): void => handleConfirmShow(false) }
            />
        </>
    );
}
